import React, {useCallback, useEffect, useMemo, useState} from "react";
import Dialog from "../../base/dialog";
import {Box, DialogActions, DialogContent, DialogTitle, Divider, Typography} from "@mui/material";
import Button from "../../base/button/button";
import TopBarProgressIndicator from "../../app-specific/topbar-progress-indicator";
import Api from "../../../../core/services/api/api";
import useIsMounted from "../../../hooks/use-is-mounted";
import DuplicateOrderDialogConfigureSection from "./sections/configure";
import DuplicateOrderDialogReviewResultsSection from "./sections/review-results";
import ImportingLottieAnimation from "../../app-specific/lottie-animations/importing";
import useAccessLevels from "../../../hooks/use-access-levels";
import {CustomerAddressTypes} from "../../../../core/constants/enums";
import ToastService from "../../../../core/services/toast";

export const DuplicateOrderVariants = {
    Quotes: 'quotes',
    SalesOrders: 'salesOrders',
    Invoices: 'invoices',
    Credits: 'credits',
}

const steps = {
    configure: 'configure',
    duplicating: 'duplicating',
    reviewResults: 'reviewResults',
};

const processTabs = {
    configure: 'Configure',
    duplicating: 'Duplicating',
    reviewResults: 'Review Results',
}

const initialFlags = {
    orderInfo: {po: false, fob: false, requiredDate: false, referenceNo: false, territory: false, accountManager: true, paymentTerms: false},
    customer: true, // true means keep original customer
    newCustomer: null,
    products: true,
    notes: true,
    shippingAddress: false,
    billingAddress: false,
    bypass: false,
    transformToSalesOrder: false,
    dimensions: false,
    shippingInfo: false,
}

const DuplicateOrderDialog = ({open, close: closeProps, variant, recordId, customerId}) => {
    const [step, setStep] = useState(steps.configure);
    const [sourceData, setSourceData] = useState(null);
    const [flags, setFlags] = useState(initialFlags);
    const [duplicationProgress, setDuplicationProgress] = useState(null); // String describing current action
    const [createdOrderDetails, setCreatedOrderDetails] = useState(null); // Details for the Review tab
    const [loadingSource, setLoadingSource] = useState(false);
    const operatorAccessLevels = useAccessLevels();

    const isMounted = useIsMounted();

    const currentProcess = useMemo(() => {
        switch (step) {
            case steps.configure:
                return processTabs.configure;
            case steps.duplicating:
                return processTabs.duplicating;
            case steps.reviewResults:
                return processTabs.reviewResults;
            default:
                return processTabs.configure;
        }
    }, [step])

    const visibleProcessTabs = useMemo(() => {
        return [processTabs.configure, processTabs.duplicating, processTabs.reviewResults];
    }, []);

    const passed = useCallback((currentType, type) => {
        switch (currentType) {
            case processTabs.configure:
            default:
                return false
            case processTabs.duplicating:
                return (type === processTabs.configure);
            case processTabs.reviewResults:
                return (type === processTabs.configure || type === processTabs.duplicating);
        }
    }, [])

    /**
     * Resets the state of the dialog to its initial values.
     * @type {(function(): void)|*}
     */
    const resetState = useCallback(() => {
        setStep(steps.configure);
        setSourceData(null);
        setFlags(initialFlags);
        setDuplicationProgress(null);
        setCreatedOrderDetails(null);
    }, []);

    /**
     * Fetches the source data for the duplicate order.
     * @type {(function(): Promise<void>)}
     */
    const fetchSourceData = useCallback(async () => {
        if (!customerId || !recordId || !variant) return;
        setLoadingSource(true);

        let response;
        if (variant === DuplicateOrderVariants.Quotes) {
            response = await Api.getQuoteInformationForCustomer(customerId, recordId);
        } else if (variant === DuplicateOrderVariants.SalesOrders) {
            response = await Api.getSalesOrderInformationForCustomer(customerId, recordId);
        } else if (variant === DuplicateOrderVariants.Invoices) {
            response = await Api.getInvoiceInformationForCustomer(customerId, recordId);
        } else if (variant === DuplicateOrderVariants.Credits) {
            response = await Api.getCreditInformationForCustomer(customerId, recordId);
        }

        if (!isMounted()) return;

        if (response?.resultFlag) {
            // Fetch everything we might need immediately.
            let shippingInfo = null, containers = [], orderDimensions = [];

            // Always fetch pallets, dimensions, and shipping info if we have access to order id or invoice id based on variant
            const forApiIds = {};
            if (variant === DuplicateOrderVariants.Invoices || variant === DuplicateOrderVariants.Credits) forApiIds.invoiceId = recordId;
            else forApiIds.orderId = recordId;

            const [shippingResponse, containersResponse, partsResponse] = await Promise.all([
                Api.getFreightOrderInformation(forApiIds),
                Api.getOrderPalletsList(forApiIds),
                Api.getOrderDimensionsList(forApiIds)
            ]);

            if (shippingResponse?.resultFlag) shippingInfo = shippingResponse.data;
            if (containersResponse?.resultFlag) containers = containersResponse.data ?? [];
            if (partsResponse?.resultFlag) orderDimensions = partsResponse.data ?? [];

            setSourceData({
                ...response.data,
                customerInfo: response.data.customer, // Save inner details carefully
                shippingInfo,
                containers,
                orderDimensions,
            });
        }
        setLoadingSource(false);
    }, [customerId, recordId, variant, isMounted]);

    /**
     * Fetches the source data for the duplicate order.
     */
    useEffect(() => {
        if (!open) {
            resetState();
        } else {
            void fetchSourceData();
        }
    }, [open, resetState, fetchSourceData]);

    /**
     * Closes the dialog and passes the created order details to the parent component.
     * @type {(function(): void)|*}
     */
    const close = useCallback(() => {
        closeProps(!!createdOrderDetails);
    }, [closeProps, createdOrderDetails]);

    const dialogSize = useMemo(() => {
        return 'md'; // 'sm' or 'md' depending on the step
    }, []);

    const dialogActions = useMemo(() => {
        switch (step) {
            case steps.configure:
                return {
                    show: true,
                    left: [{
                        onClick: () => close(),
                        text: "Cancel",
                    }],
                    right: [
                        {
                            onClick: () => setStep(steps.duplicating),
                            text: "Duplicate",
                            disabled: loadingSource || !sourceData,
                        }
                    ]
                };
            case steps.duplicating:
                return {
                    show: false,
                };
            case steps.reviewResults:
                return {
                    show: true,
                    left: [],
                    right: [{
                        onClick: () => close(),
                        text: "Done",
                    }]
                }
            default:
                return {
                    show: false,
                }
        }
    }, [step, close, loadingSource, sourceData])

    /**
     * Saves the freight/shipping information of the newly created order.
     * Uses the same payload structure as `updateOrdersShippingInformation` in the quotes single view.
     *
     * Note: `freightInfoId` and `freeFreightId` are intentionally omitted since the new order
     * does not yet have an existing freight record — the API will create one.
     *
     * @type {(function(number, number): Promise<void>)|*}
     */
    const saveOrderFreightInfo = useCallback(async (orderId) => {
        const info = sourceData?.shippingInfo;
        if (!info) return;
        const freightCompaniesResponse = await Api.getFreightCompanies({})
        const freightCompany = freightCompaniesResponse?.data?.find(e => e.id === info.freightCompanyId)
        if (!freightCompany) return;
        await Api.saveOrderFreightInfo({
            orderId: orderId,
            freightCompanyInfo: freightCompany,
            freightFee: Number(info.customerFee ?? 0),
            trackingNo: info.trackingNumber ?? '',
            estimatedDeliveryDateTime: info.estimatedDeliveryDateTime ?? null,
            estimatedFee: Number(info.estimatedFee ?? 0),
            // freightInfoId & freeFreightId omitted: new order has no pre-existing freight record
        });
    }, [sourceData?.shippingInfo])

    /**
     * Duplicates the order based on the selected flags.
     *
     * 1. Create Quote
     * 2. Order Info
     * 3. Addresses
     * 4. Notes
     * 5. Bypass Info
     * 6. Transform to Sales Order
     * 7. Shipping Info
     * 8. Pallets and Dimensions
     *
     * @type {(function(): Promise<void>)|*}
     */
    const duplicateProcess = useCallback(async () => {
        const targetCustomerId = flags.customer ? customerId : flags.newCustomer?.id;
        if (!targetCustomerId) {
            setDuplicationProgress("Target Customer is invalid.");
            setTimeout(() => setStep(steps.configure), 2000);
            return;
        }

        try {
            // 1. Create Quote
            setDuplicationProgress("Creating new Quote...");

            const createQuoteResponse = await Api.createNewQuoteV2({
                customerId: targetCustomerId,
                parts: (flags.products && sourceData?.items?.length) ? sourceData.items.map(e => ({
                    partNumber: e.partNo,
                    qty: Number(e.orderQty),
                    price: e.unitPriceBeforeDiscount,
                    ...(operatorAccessLevels.packages.sales.manageWarehousesInQuote && {
                        warehouseCode: e.whse,
                    }),
                })) : [],
            });

            if (!createQuoteResponse?.resultFlag) {
                // noinspection ExceptionCaughtLocallyJS
                throw new Error("Failed to create new quote.");
            }

            const newQuoteId = createQuoteResponse.data?.internalId;
            const newQuoteOrderNo = createQuoteResponse.data?.orderNumber;

            setDuplicationProgress("Fetching newly created Quote details...");
            const quoteInformationResponse = await Api.getQuoteInformationForCustomer(targetCustomerId, newQuoteId, false);
            const quoteInformation = quoteInformationResponse?.data;
            if (!quoteInformation) {
                // noinspection ExceptionCaughtLocallyJS
                throw new Error("Failed to fetch newly created Quote details.");
            }

            if (flags.products && sourceData?.items?.length) {
                for (const item of sourceData.items) {
                    const itemIndex = sourceData.items.indexOf(item);
                    const quoteInformationItem = quoteInformation?.items?.[itemIndex];
                    if (quoteInformationItem?.description !== item.description) {
                        setDuplicationProgress(`Applying description for Part: ${item.partNo}...`);
                        await Api.updateQuoteItems({
                            customerId: targetCustomerId,
                            orderId: newQuoteId,
                            PartCountChangedList: [
                                {
                                    partNo: item.partNo,
                                    finalCount: Number(item.orderQty),
                                    unitPrice: item.unitPriceBeforeDiscount,
                                    whs: item.whse,
                                    partDescription: item?.description,
                                }
                            ],
                        });
                    }
                    if (Number(item.discountPct) > 0) {
                        setDuplicationProgress(`Applying discount for Part: ${item.partNo}...`);
                        await Api.updateQuoteItemDiscount({
                            customerId: targetCustomerId,
                            orderId: newQuoteId,
                            discountPercentage: Number(item.discountPct),
                            partNo: item.partNo,
                        });
                    }
                }
            }

            // 2. Order Info
            if (Object.values(flags.orderInfo).some(Boolean) && sourceData) {
                setDuplicationProgress("Copying Order Details...");
                await Api.updateQuotesPOAndFob({
                    customerId: targetCustomerId,
                    orderId: newQuoteId,
                    customerPo: flags.orderInfo.po ? (sourceData.customerPO ?? '') : (quoteInformation?.customerPO ?? ''),
                    paymentTerms: flags.orderInfo.paymentTerms ? (sourceData.termsCode ?? sourceData.paymentTermsCode ?? '') : (quoteInformation?.termsCode ?? quoteInformation?.paymentTermsCode ?? ''),
                    fob: flags.orderInfo.fob ? (sourceData.fob ?? '') : (quoteInformation?.fob ?? ''),
                    requiredDate: flags.orderInfo.requiredDate ? (sourceData.requiredDate ?? null) : null,
                    referenceNo: flags.orderInfo.referenceNo ? (sourceData.referenceNo ?? '') : (quoteInformation?.referenceNo ?? ''),
                    territoryCode: flags.orderInfo.territory ? (sourceData.territory?.code ?? sourceData.territory ?? null) : (quoteInformation?.territory?.code ?? quoteInformation?.territory ?? null),
                    accountManagerCode: flags.orderInfo.accountManager ? (sourceData.accountManager?.code ?? sourceData.accountManager ?? sourceData.accountManagerNo) : (quoteInformation?.accountManager?.code ?? quoteInformation?.accountManager ?? quoteInformation?.accountManagerNo),
                });
            }

            // 3. Addresses
            if (flags.shippingAddress || flags.billingAddress) {
                if (flags.shippingAddress) {
                    setDuplicationProgress("Copying Shipping Address...");
                    await Api.updateQuoteAddress({
                        customerId: targetCustomerId,
                        orderId: newQuoteId,
                        address: {...sourceData.shippingAddress, type: {id: CustomerAddressTypes.shipping},},
                    });
                }
                if (flags.billingAddress) {
                    setDuplicationProgress("Copying Billing Address...");
                    await Api.updateQuoteAddress({
                        customerId: targetCustomerId,
                        orderId: newQuoteId,
                        address: {...sourceData.billingAddress, type: {id: CustomerAddressTypes.billing},},
                    });
                }
            }

            // 4. Notes
            if (flags.notes) {
                setDuplicationProgress("Fetching and Copying Notes...");
                const notesIdentifier = (variant === DuplicateOrderVariants.Quotes || variant === DuplicateOrderVariants.SalesOrders)
                    ? sourceData.orderNo
                    : sourceData.invoiceNo;

                if (notesIdentifier) {
                    const notesResponse = await Api.getOrderNotes(notesIdentifier);
                    if (notesResponse?.resultFlag && notesResponse.data?.length) {
                        for (const note of notesResponse.data) {
                            await Api.createOrderNote({
                                subject: note.subject ?? '',
                                body: note.body ?? '',
                                isAutoDefine: !!note.isAutoDefine,
                                printPlaceId: note.printPlace?.id,
                                noteTypeId: note.type?.id,
                                orderNo: newQuoteOrderNo,
                            });
                        }
                    }
                }
            }

            // 5. Bypass Info
            if (flags.bypass && (variant === DuplicateOrderVariants.Quotes || variant === DuplicateOrderVariants.SalesOrders)) {
                setDuplicationProgress("Copying Bypass Information...");
                const bypassResponse = await Api.getQuoteBypassInformation(recordId);
                // getQuoteBypassInformation returns the active bypass object if it exists.
                if (bypassResponse?.resultFlag && bypassResponse?.data) {
                    const bypassInfo = bypassResponse.data;
                    // fetch reasons to find the reasonId
                    const reasonsResponse = await Api.getQuoteBypassReasons();
                    const reasonId = reasonsResponse?.data?.find(reason => reason.title === bypassInfo.reason)?.id;
                    if (reasonId) {
                        await Api.submitQuoteBypass({
                            orderId: newQuoteId, // Passing orderId to endpoint payload struct.
                            reasonId: reasonId,
                        });
                    }
                }
            }

            // 8. Shipping info of Quote
            if (flags.shippingInfo && sourceData.shippingInfo
                && operatorAccessLevels.packages.sales.manageQuotes
                && operatorAccessLevels.packages.sales.quoteFreightInfo) {
                setDuplicationProgress("Copying Shipping Info...");
                await saveOrderFreightInfo(newQuoteId, targetCustomerId)
            }

            // 7. Transform to Sales Order
            let createdOrder = {id: newQuoteId, type: DuplicateOrderVariants.Quotes, targetCustomerId};
            if (flags.transformToSalesOrder) {
                setDuplicationProgress("Transforming to Sales Order...");
                const transformResponse = await Api.generateSalesOrder({
                    customerId: targetCustomerId,
                    orderId: newQuoteId,
                    userToBackOrderPermission: true,
                    note: "",
                }, false);

                if (!transformResponse?.resultFlag) {
                    ToastService.error({
                        message: transformResponse?.message ?? 'An error occurred while creating the sales order.',
                        title: 'Sales Order Creation Error',
                        hideTicketSubmission: true,
                    })
                } else {
                    const newSalesOrderId = newQuoteId;
                    createdOrder = {id: newSalesOrderId, type: DuplicateOrderVariants.SalesOrders, targetCustomerId};

                    // 8. Shipping info of Sales order
                    if (flags.shippingInfo && sourceData.shippingInfo && newSalesOrderId
                        && !(operatorAccessLevels.packages.sales.manageQuotes
                            && operatorAccessLevels.packages.sales.quoteFreightInfo)
                        && operatorAccessLevels.packages.sales.manageSalesOrders
                        && operatorAccessLevels.packages.sales.orderFreightInfo) {
                        setDuplicationProgress("Copying Shipping Info...");
                        await saveOrderFreightInfo(newSalesOrderId, targetCustomerId)
                    }

                    // 8. Pallets and Dimensions
                    if (flags.dimensions && (sourceData.containers?.length || sourceData.orderDimensions?.length)) {
                        setDuplicationProgress("Copying Pallets...");

                        // Map of old pallet ID to new created pallet ID
                        const palletIdMap = {};

                        // Create the containers/pallets first
                        for (const oldContainer of (sourceData.containers ?? [])) {
                            const containerResponse = await Api.createOrderPallet({
                                orderId: newSalesOrderId,
                                title: oldContainer.title,
                                length: oldContainer.length,
                                width: oldContainer.width,
                                height: oldContainer.height,
                                weight: oldContainer.weight,
                                type: oldContainer.type,
                                status: oldContainer.status || "",
                                trackingNumber: oldContainer.trackingNumber || "",
                            });
                            if (containerResponse?.resultFlag && containerResponse?.data?.id) {
                                palletIdMap[oldContainer.id] = containerResponse.data.id;
                            }
                        }
                    }
                }
            }

            setCreatedOrderDetails(createdOrder);
            setStep(steps.reviewResults);
        } catch (err) {
            console.error("Duplication failed:", err);
            setDuplicationProgress("Failed: " + err.message);
            setTimeout(() => setStep(steps.configure), 3000);
        }
    }, [customerId, flags.billingAddress, flags.bypass, flags.customer, flags.dimensions, flags.newCustomer?.id, flags.notes, flags.orderInfo, flags.products, flags.shippingAddress, flags.shippingInfo, flags.transformToSalesOrder, operatorAccessLevels.packages.sales.manageWarehousesInQuote, operatorAccessLevels.packages.sales.manageQuotes, operatorAccessLevels.packages.sales.quoteFreightInfo, operatorAccessLevels.packages.sales.manageSalesOrders, operatorAccessLevels.packages.sales.orderFreightInfo, recordId, saveOrderFreightInfo, sourceData, variant]);

    /**
     * Automatically duplicates the order when the step is "duplicating".
     */
    useEffect(() => {
        if (step !== steps.duplicating) return;
        void duplicateProcess();
    }, [duplicateProcess, step]);


    return (
        <Dialog
            open={open}
            setOpen={() => close()}
            maxWidth={dialogSize}
            className={'bound-dialog duplicate-order-dialog'}
            disableBackdropClick={step === steps.duplicating}
        >
            <DialogTitle>
                <TopBarProgressIndicator
                    currentType={currentProcess}
                    types={visibleProcessTabs}
                    passed={passed}
                />
            </DialogTitle>
            <DialogContent className={'mt--4 pb-4'}>
                {
                    step === steps.configure &&
                    <DuplicateOrderDialogConfigureSection
                        flags={flags}
                        setFlags={setFlags}
                        variant={variant}
                        loadingSource={loadingSource}
                    />
                }
                {
                    step === steps.duplicating &&
                    <Box className={'d-flex flex-column align-items-center justify-content-center px-4 mt-4 mb-3'} sx={{gap: 3}}>
                        <ImportingLottieAnimation width={'80%'}/>
                        <Typography variant="body1" color="textSecondary" fontWeight={500}>
                            {duplicationProgress || "Processing..."}
                        </Typography>
                    </Box>
                }
                {
                    step === steps.reviewResults &&
                    <DuplicateOrderDialogReviewResultsSection createdOrderDetails={createdOrderDetails}/>
                }
            </DialogContent>
            {
                dialogActions.show &&
                <DialogActions className={'p-0 w-100 d-flex flex-column'}>
                    <Divider className='divider'/>
                    <div className={'d-flex flex-wrap w-100 justify-content-between align-items-center m-0'}>
                        <div className={'d-flex align-items-center justify-content-start flex-grow-1'}>
                            {
                                !!dialogActions.left?.length &&
                                dialogActions.left.map((action, i, array) => (
                                    <Button
                                        key={i}
                                        className='px-4 py-2-5'
                                        variant={'dialog'}
                                        color={'primary'}
                                        onClick={action.onClick}
                                        disabled={!!action.disabled}
                                        borderRadius={`0 ${i === array.length - 1 ? '10px' : '0'} 0 ${i === 0 ? '10px' : '0'}`}
                                    >
                                        {action.text ?? ''}
                                    </Button>
                                ))
                            }
                        </div>
                        <div className={'d-flex align-items-center justify-content-end flex-grow-1'}>
                            {
                                !!dialogActions.right?.length &&
                                dialogActions.right.map((action, i, array) => (
                                    <Button
                                        key={i}
                                        className=' px-4 py-2-5'
                                        variant={'dialog'}
                                        color={'primary'}
                                        onClick={action.onClick}
                                        disabled={!!action.disabled}
                                        borderRadius={`${i === 0 ? '10px' : '0'} 0 ${i === array.length - 1 ? '10px' : '0'} 0`}
                                    >
                                        {action.text ?? ''}
                                    </Button>
                                ))
                            }
                        </div>
                    </div>
                </DialogActions>
            }
        </Dialog>
    );

}

export default DuplicateOrderDialog;
